/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { EntityVersion, ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  identity,
  none,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { parametrizeUrl } from '@gh/core-util';
import { omit } from 'lodash';
import { Observable } from 'rxjs';
import { ScheduleDetailsDto } from '../schedule-templates/schedule-template.dto';
import {
  PreConstructionScheduleFilterDto,
  PreConstructionScheduleListItemDto,
  PreConstructionWorksheetDto,
  PreConstructionWorksheetInitDto,
  PreConstructionWorksheetSaveDto,
  ScheduleTaskDetailsDto,
} from './schedule.dto';
import {
  PreConstructionScheduleListItemMapper,
  PreConstructionWorksheetMapper,
  PreConstructionWorksheetSaveMapper,
  ScheduleDetailsMapper,
  ScheduleTaskDetailsMapper,
} from './schedule.mapper';
import { Confirmation } from '@gh/core-ui';

@Injectable()
export class PreConstructionScheduleRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  findAllAsDataSource(filter: PreConstructionScheduleFilterDto): SlicedDataSource<PreConstructionScheduleListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/schedules/pre-constructions`,
      filter,
      identity(),
      arrayOf(PreConstructionScheduleListItemMapper));
  }

  findAllAsFinderDataSource(filter: PreConstructionScheduleFilterDto): SlicedDataSource<PreConstructionScheduleListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/schedules/pre-constructions/finder`,
      filter,
      identity(),
      arrayOf(PreConstructionScheduleListItemMapper));
  }

  remove(scheduleId: ID, version: EntityVersion): Observable<void> {
    return this.httpDelete(
      parametrizeUrl(`${this.bp}/schedules/pre-constructions/${scheduleId}`, { version }),
      void 0,
      none(),
      none());
  }

  init(jobId: ID, templateId: ID): Observable<ScheduleDetailsDto> {
    return this.httpGet(`${this.bp}/schedules/pre-constructions/init`, { jobId, templateId }, identity(), ScheduleDetailsMapper);
  }

  get(jobId: ID): Observable<ScheduleDetailsDto> {
    return this.httpGet(`${this.bp}/schedules/pre-constructions/${jobId}`, ScheduleDetailsMapper);
  }

  getCombined(jobIds: ID[]): Observable<ScheduleDetailsDto[]> {
    return this.httpGet(
      parametrizeUrl(`${this.bp}/schedules/pre-constructions/combined-gantt-chart`, { id: jobIds }),
      arrayOf(ScheduleDetailsMapper));
  }

  create(schedule: ScheduleDetailsDto, saveBaseDatesAnswer?: Confirmation): Observable<ScheduleDetailsDto> {
    let url = `${this.bp}/schedules/pre-constructions`;
    if (saveBaseDatesAnswer) {
      url = `${url}?saveBaseDates=${saveBaseDatesAnswer === Confirmation.Yes ? 'true' : 'false'}`;
    }
    return this.httpPost(url, schedule, ScheduleDetailsMapper);
  }

  update(schedule: ScheduleDetailsDto, saveBaseDatesAnswer?: Confirmation): Observable<ScheduleDetailsDto> {
    let url = `${this.bp}/schedules/pre-constructions/${schedule.scheduleId}`;
    if (saveBaseDatesAnswer) {
      url = `${url}?saveBaseDates=${saveBaseDatesAnswer === Confirmation.Yes ? 'true' : 'false'}`;
    }
    return this.httpPut(url, schedule, ScheduleDetailsMapper);
  }

  recalculate(schedule: ScheduleDetailsDto): Observable<ScheduleDetailsDto> {
    return this.httpPut(`${this.bp}/schedules/pre-constructions/recalculate`, schedule, ScheduleDetailsMapper);
  }

  getTask(scheduleId: ID, taskId: ID): Observable<ScheduleTaskDetailsDto> {
    return this.httpGet(`${this.bp}/schedules/pre-constructions/${scheduleId}/tasks/${taskId}`, ScheduleTaskDetailsMapper);
  }

  updateTask(scheduleId: ID, taskId: ID, task: ScheduleTaskDetailsDto, saveBaseDatesAnswer?: Confirmation):
    Observable<ScheduleTaskDetailsDto> {
    let url = `${this.bp}/schedules/pre-constructions/${scheduleId}/tasks/${taskId}`;
    if (saveBaseDatesAnswer) {
      url = `${url}?saveBaseDates=${saveBaseDatesAnswer === Confirmation.Yes ? 'true' : 'false'}`;
    }
    return this.httpPut(url, task, ScheduleTaskDetailsMapper);
  }

  initWorksheet(): Observable<PreConstructionWorksheetInitDto> {
    return this.httpGet(`${this.bp}/schedules/pre-constructions/worksheet/init`, identity());
  }

  getWorksheet(templateId: ID, queryParams?: any): Observable<PreConstructionWorksheetDto> {
    return this.httpGet(
      `${this.bp}/schedules/pre-constructions/worksheet`,
      { templateId, ...queryParams },
      identity(),
      PreConstructionWorksheetMapper);
  }

  saveWorksheet(worksheet: PreConstructionWorksheetSaveDto): Observable<void> {
    return this.httpPost(
      parametrizeUrl(`${this.bp}/schedules/pre-constructions/worksheet`, { templateId: worksheet.templateId }),
      omit(worksheet, 'templateId'),
      PreConstructionWorksheetSaveMapper,
      none());
  }
}
